import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import "../../../Assets/css/style.css";
import MainLayout from "../../../Partials/MainLayout";
import FeedLayout from "../../../Partials/FeedLayout";

// const BASE_URL = 'http://localhost/business_community/';
const BASE_URL = 'https://qnexus.in/community/';
const avatarLetter = (name = '') => name.trim().charAt(0).toUpperCase() || '?';

/* ── Debounce hook ── */
function useDebounce(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const VENDOR_STATUS_CLASS = {
  INVITED:  "vs-invited",
  ACCEPTED: "vs-accepted",
  REJECTED: "vs-rejected",
};

const InviteVendor = () => {
  const { id: event_id } = useParams();
  const navigate         = useNavigate();

  const [serviceType,    setServiceType]    = useState("");
  const [inviting,       setInviting]       = useState(null); // user_id being invited
  const [selectedVendor, setSelectedVendor] = useState(null);

  // User search
  const [vendorSearch,    setVendorSearch]    = useState("");
  const [vendorResults,   setVendorResults]   = useState([]);
  const [vendorSearching, setVendorSearching] = useState(false);

  // Invited vendors list
  const [invitedVendors, setInvitedVendors] = useState([]);

  const debouncedVendorSearch = useDebounce(vendorSearch, 350);

  /* ── Fetch already invited vendors ── */
  const fetchVendors = useCallback(async () => {
    const res = await Handler({
      method: "GET",
      url: `/events/event_vendor_list.php?event_id=${event_id}&page=1&limit=50`,
    });
    if (res.success) setInvitedVendors(res.data?.list || []);
  }, [event_id]);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  /* ── Search users ── */
  useEffect(() => {
    if (!debouncedVendorSearch.trim()) { setVendorResults([]); return; }

    let cancelled = false;
    (async () => {
      setVendorSearching(true);
      try {
        const res = await Handler({
          method: "GET",
          url: `/users/search_list.php?search=${encodeURIComponent(debouncedVendorSearch)}&limit=10`,
        });
        if (!cancelled && res?.success) {
          setVendorResults(res.data?.list || []);
        }
      } catch {
        // silent
      } finally {
        if (!cancelled) setVendorSearching(false);
      }
    })();

    return () => { cancelled = true; };
  }, [debouncedVendorSearch]);

  /* ── Select vendor from search results ── */
  const handleSelectVendor = (user) => {
    setSelectedVendor(user);
    setVendorSearch(user.business_name);
    setVendorResults([]);
  };

  /* ── Send invite ── */
  const handleInvite = async (e) => {
    e.preventDefault();
    if (!selectedVendor) {
      toast.error("Please select a vendor from the search results");
      return;
    }

    setInviting(selectedVendor.user_id);
    const res = await Handler({
      method: "POST",
      url: "/events/invite_vendor.php",
      data: {
        event_id:     parseInt(event_id),
        vendor_id:    parseInt(selectedVendor.user_id),
        service_type: serviceType,
      },
    });
    setInviting(null);

    if (res.success) {
      toast.success("Vendor invited successfully!");
      setSelectedVendor(null);
      setVendorSearch("");
      setServiceType("");
      fetchVendors();
    } else {
      toast.error(res.message || "Failed to invite vendor");
    }
  };

  return (
    <MainLayout>
      <FeedLayout>
        <div className="ev-page">
          <button className="ev-btn-back" onClick={() => navigate(`/user/event/${event_id}`)}>
            <i className="bx bx-arrow-back" /> Back to Event
          </button>

          <div className="ev-header mt-2">
            <div>
              <h1 className="ev-title">Invite Vendor</h1>
              <p className="ev-subtitle">Send an invitation to a community member to join this event</p>
            </div>
          </div>

          <div className="ev-invite-layout">

            {/* Invite Form */}
            <div className="ev-invite-form-wrap">
              <div className="ev-form-card">
                <h3 className="ev-form-card-title">
                  <i className="bx bx-user-plus" /> Send Invite
                </h3>
                <form onSubmit={handleInvite}>

                  {/* Vendor Search */}
                  <div className="ev-form-section" style={{ position: "relative" }}>
                    <label className="ev-label">
                      Search Vendor <span className="ev-required">*</span>
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        className="ev-input"
                        type="text"
                        placeholder="Search by business name, city or phone…"
                        value={vendorSearch}
                        onChange={(e) => {
                          setVendorSearch(e.target.value);
                          setSelectedVendor(null);
                        }}
                        autoComplete="off"
                      />
                      {vendorSearching && (
                        <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }}>
                          <i className="bx bx-loader-alt bx-spin" style={{ color: "var(--c-muted)" }} />
                        </span>
                      )}
                    </div>

                    {/* Dropdown results */}
                    {vendorResults.length > 0 && !selectedVendor && (
                      <div className="chat-user-list" style={{
                        position: "absolute", zIndex: 100, width: "100%",
                        background: "#fff", border: "1px solid #e5e7eb",
                        borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                        maxHeight: 260, overflowY: "auto", marginTop: 4,
                      }}>
                        {vendorResults.map((user) => (
                          <div
                            key={user.user_id}
                            className="chat-user-item"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleSelectVendor(user)}
                          >
                            <div className="chat-avatar" style={{ flexShrink: 0 }}>
                              <div className="chat-avatar__img">
                                {user.logo
                                  ? <img
                                      src={`${BASE_URL}${user.logo}`}
                                      alt={user.business_name}
                                      onError={e => { e.target.style.display = 'none'; }}
                                    />
                                  : avatarLetter(user.business_name)
                                }
                              </div>
                            </div>
                            <div className="chat-user-item__info">
                              <span className="chat-user-item__name">{user.business_name}</span>
                              {(user.city || user.area) && (
                                <span className="chat-user-item__meta">
                                  📍 {[user.area, user.city].filter(Boolean).join(', ')}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* No results */}
                    {!vendorSearching && vendorSearch.trim() && vendorResults.length === 0 && !selectedVendor && (
                      <p style={{ fontSize: "0.8rem", color: "var(--c-muted)", marginTop: 6 }}>
                        No vendors found for "{vendorSearch}"
                      </p>
                    )}

                    <span className="ev-hint">
                      <i className="bx bx-info-circle" /> The vendor must be a member of the linked community
                    </span>
                  </div>

                  {/* Selected vendor preview */}
                  {selectedVendor && (
                    <div style={{
                      display: "flex", alignItems: "center", gap: 10,
                      background: "#f0fdf4", border: "1px solid #bbf7d0",
                      borderRadius: 8, padding: "10px 14px", marginBottom: 12,
                    }}>
                      <div className="chat-avatar" style={{ flexShrink: 0 }}>
                        <div className="chat-avatar__img">
                          {selectedVendor.logo
                            ? <img
                                src={`${BASE_URL}${selectedVendor.logo}`}
                                alt={selectedVendor.business_name}
                                onError={e => { e.target.style.display = 'none'; }}
                              />
                            : avatarLetter(selectedVendor.business_name)
                          }
                        </div>
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{selectedVendor.business_name}</div>
                        {(selectedVendor.city || selectedVendor.area) && (
                          <div style={{ fontSize: "0.78rem", color: "var(--c-muted)" }}>
                            📍 {[selectedVendor.area, selectedVendor.city].filter(Boolean).join(', ')}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "var(--c-muted)" }}
                        onClick={() => { setSelectedVendor(null); setVendorSearch(""); }}
                      >
                        <i className="bx bx-x" style={{ fontSize: 18 }} />
                      </button>
                    </div>
                  )}

                  {/* Service Type */}
                  <div className="ev-form-section">
                    <label className="ev-label">Service Type</label>
                    <input
                      className="ev-input"
                      value={serviceType}
                      onChange={(e) => setServiceType(e.target.value)}
                      placeholder="e.g. Catering, Photography, Security..."
                    />
                  </div>

                  <div className="ev-form-actions">
                    <button
                      type="submit"
                      className="ev-btn-primary w-full"
                      disabled={!!inviting || !selectedVendor}
                    >
                      {inviting ? (
                        <><i className="bx bx-loader-alt bx-spin" /> Sending...</>
                      ) : (
                        <><i className="bx bx-send" /> Send Invitation</>
                      )}
                    </button>
                  </div>

                </form>
              </div>
            </div>

            {/* Invited Vendors List */}
            <div className="ev-invited-list">
              <h3 className="ev-form-card-title">
                <i className="bx bx-group" /> Vendors ({invitedVendors.length})
              </h3>

              {invitedVendors.length === 0 ? (
                <div className="ev-vendor-empty">
                  <i className="bx bx-user-x" />
                  <p>No vendors invited yet</p>
                </div>
              ) : (
                <div className="ev-invited-cards">
                  {invitedVendors.map((v) => (
                    <div key={v.event_vendor_id} className="ev-invited-card">
                      <div className="ev-vendor-avatar">{v.name?.charAt(0)?.toUpperCase()}</div>
                      <div className="ev-invited-info">
                        <div className="ev-invited-name">{v.name}</div>
                        <div className="ev-invited-service">
                          {v.service_type || <span className="ev-na">No service type</span>}
                        </div>
                        <div className="ev-invited-mobile">
                          <i className="bx bx-phone" /> {v.vendor_mobile || "—"}
                        </div>
                      </div>
                      <span className={`ev-vendor-status ${VENDOR_STATUS_CLASS[v.status]}`}>
                        {v.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </FeedLayout>
    </MainLayout>
  );
};

export default InviteVendor;